import { z } from "zod";

/**
 * `Message.toolCalls` — the tool invocations an AI row asked for.
 *
 * Written by `Message.endMessage()` straight from the provider's assistant
 * message, so the stored rows carry the OpenAI `ChatCompletionMessageToolCall`
 * shape: an id, a discriminator, and a `function` object whose `arguments` is a
 * JSON **string** the model produced.
 *
 * This is the ONLY record of what an agent asked a tool to send. Channel tools
 * (`send_message` on WhatsApp, the embed card twin) hand their payload to the
 * provider and never write a `Message` row of their own, so the row's
 * `textContent` describes the tool's RESULT ("Success") and never its input.
 * A read API that projects `textContent` alone therefore cannot show what the
 * customer received — which is what this schema exists to make expressible.
 */
export const StoredMessageToolCallSchema = z.object({
  id: z.string().optional(),
  type: z.string().optional(),
  function: z
    .object({
      name: z.string().optional(),
      arguments: z.string().optional()
    })
    .optional()
});

/**
 * The wire projection of one tool call: flat, three fields, no provider
 * discriminator.
 *
 * `arguments` stays the raw JSON string the model emitted rather than a parsed
 * object. Models do produce malformed JSON, and parsing server-side would make
 * a read endpoint fail on data it merely reports; handing the caller the exact
 * bytes lets them parse and handle that themselves, which is the convention
 * every OpenAI-shaped consumer already knows.
 */
export const MessageToolCallSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  arguments: z.string().nullable()
});

export type MessageToolCall = z.infer<typeof MessageToolCallSchema>;

/**
 * Project a stored `Message.toolCalls` column onto the wire shape.
 *
 * Degrades per ENTRY rather than per column: one unparseable row does not blank
 * the rest, and a column that is not an array at all yields `[]`. An empty
 * array is a usable value of the right type here — the column's own Prisma
 * default — so no caller has to distinguish "none" from "unreadable".
 */
export function toMessageToolCalls(raw: unknown): MessageToolCall[] {
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((entry): MessageToolCall[] => {
    const parsed = StoredMessageToolCallSchema.safeParse(entry);
    if (!parsed.success) return [];

    return [
      {
        id: parsed.data.id ?? null,
        name: parsed.data.function?.name ?? null,
        arguments: parsed.data.function?.arguments ?? null
      }
    ];
  });
}
